import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Cell,
  Column,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
  Tag,
  buttonLinkProps,
} from '@wodi/ui';
import type { ReactNode } from 'react';

import {
  purchaseOrdersQuery,
  purchaseRequisitionsQuery,
  supplierInvoicesQuery,
} from '../api/procurement.ts';
import { can, sessionQuery } from '../api/session.ts';
import { searchSuppliers } from '../api/stock.ts';
import { TableScroll } from '../layout/screen.tsx';
import { formatIsoDate } from '../patients/format.ts';
import { LoadFailed, ProcurementTabs } from '../procurement/common.tsx';
import { documentLabel, documentTag, orderStateWords } from '../procurement/format.ts';
import { formatCents } from '../stock/format.ts';
import styles from '../stock/stock.module.css';

// The procurement area (docs/plans/phase2-procurement.md §5.1): LPOs, purchase
// requisitions, supplier invoices and supplier statements, one view each.

function Frame({ actions, children }: { actions?: ReactNode; children: ReactNode }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  if (session === null || !can(session, 'procurement.read'))
    return (
      <>
        <PageHeader title="Procurement" />
        <Notice tone="info">
          You don't have permission to see procurement. Ask an administrator if you need it.
        </Notice>
      </>
    );
  return (
    <div className={styles.page}>
      <PageHeader title="Procurement" />
      <div className={styles.toolbar}>
        <ProcurementTabs />
        {actions}
      </div>
      {children}
    </div>
  );
}

export function PurchaseOrdersPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const orders = useQuery(purchaseOrdersQuery());
  return (
    <Frame
      actions={
        session !== null && can(session, 'procurement.order') ? (
          <div className={styles.actions}>
            <Link to="/procurement/orders/new" {...buttonLinkProps()}>
              New LPO
            </Link>
          </div>
        ) : null
      }
    >
      <Section title="LPOs">
        {orders.isPending ? <LoadingBlock label="Loading LPOs" rows={4} /> : null}
        {orders.isError ? (
          <LoadFailed
            error={orders.error}
            fallback="LPOs couldn't be loaded."
            onRetry={() => void orders.refetch()}
          />
        ) : null}
        {orders.isSuccess ? (
          <TableScroll>
            <Table flat aria-label="LPOs">
              <TableHeader>
                <Column isRowHeader>LPO</Column>
                <Column>Supplier</Column>
                <Column>Deliver to</Column>
                <Column>Sent</Column>
                <Column numeric>Total</Column>
                <Column>State</Column>
              </TableHeader>
              <TableBody
                items={orders.data.items}
                renderEmptyState={() =>
                  'No LPOs yet. Start one from an approved purchase requisition, or with New LPO.'
                }
              >
                {(o) => (
                  <Row id={o.id}>
                    <Cell>
                      <Link to="/procurement/orders/$orderId" params={{ orderId: o.id }}>
                        <span className="wodi-id">{o.number}</span>
                      </Link>
                    </Cell>
                    <Cell>{o.supplier.name}</Cell>
                    <Cell>{o.store.name}</Cell>
                    <Cell>{formatIsoDate(o.submittedAt.slice(0, 10))}</Cell>
                    <Cell numeric>{formatCents(o.total.cents)}</Cell>
                    <Cell>
                      <Tag state={documentTag(o.status)}>
                        {orderStateWords(o.status, o.receiptStatus)}
                      </Tag>
                    </Cell>
                  </Row>
                )}
              </TableBody>
            </Table>
          </TableScroll>
        ) : null}
      </Section>
    </Frame>
  );
}

export function PurchaseRequisitionsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const reqs = useQuery(purchaseRequisitionsQuery);
  return (
    <Frame
      actions={
        session !== null && can(session, 'procurement.requisition') ? (
          <div className={styles.actions}>
            <Link to="/procurement/requisitions/new" {...buttonLinkProps()}>
              New purchase requisition
            </Link>
          </div>
        ) : null
      }
    >
      <Section title="Purchase requisitions">
        {reqs.isPending ? <LoadingBlock label="Loading requisitions" rows={4} /> : null}
        {reqs.isError ? (
          <LoadFailed
            error={reqs.error}
            fallback="Purchase requisitions couldn't be loaded."
            onRetry={() => void reqs.refetch()}
          />
        ) : null}
        {reqs.isSuccess ? (
          <TableScroll>
            <Table flat aria-label="Purchase requisitions">
              <TableHeader>
                <Column isRowHeader>Requisition</Column>
                <Column>For</Column>
                <Column>Asked by</Column>
                <Column numeric>Lines</Column>
                <Column>State</Column>
              </TableHeader>
              <TableBody
                items={reqs.data}
                renderEmptyState={() =>
                  'No purchase requisitions yet. A store asks for items to be bought here.'
                }
              >
                {(r) => (
                  <Row id={r.id}>
                    <Cell>
                      <Link
                        to="/procurement/requisitions/$requisitionId"
                        params={{ requisitionId: r.id }}
                      >
                        <span className="wodi-id">{r.number}</span>
                      </Link>
                    </Cell>
                    <Cell>{r.store.name}</Cell>
                    <Cell>{r.requestedBy.name}</Cell>
                    <Cell numeric>{r.lines.length}</Cell>
                    <Cell>
                      <Tag state={documentTag(r.status)}>{documentLabel(r.status)}</Tag>
                    </Cell>
                  </Row>
                )}
              </TableBody>
            </Table>
          </TableScroll>
        ) : null}
      </Section>
    </Frame>
  );
}

export function SupplierInvoicesPage() {
  const invoices = useQuery(supplierInvoicesQuery());
  return (
    <Frame>
      <Section title="Supplier invoices">
        {invoices.isPending ? <LoadingBlock label="Loading invoices" rows={4} /> : null}
        {invoices.isError ? (
          <LoadFailed
            error={invoices.error}
            fallback="Supplier invoices couldn't be loaded."
            onRetry={() => void invoices.refetch()}
          />
        ) : null}
        {invoices.isSuccess ? (
          <TableScroll>
            <Table flat aria-label="Supplier invoices">
              <TableHeader>
                <Column isRowHeader>Invoice</Column>
                <Column>Supplier</Column>
                <Column>Their number</Column>
                <Column>LPO</Column>
                <Column numeric>Total</Column>
                <Column>Match</Column>
                <Column>State</Column>
              </TableHeader>
              <TableBody
                items={invoices.data}
                renderEmptyState={() =>
                  'No supplier invoices yet. Record one from its LPO with Record supplier invoice.'
                }
              >
                {(i) => (
                  <Row id={i.id}>
                    <Cell>
                      <Link to="/procurement/invoices/$invoiceId" params={{ invoiceId: i.id }}>
                        <span className="wodi-id">{i.number}</span>
                      </Link>
                    </Cell>
                    <Cell>{i.supplier.name}</Cell>
                    <Cell>
                      <span className="wodi-id">{i.supplierReference}</span>
                    </Cell>
                    <Cell>
                      <span className="wodi-id">{i.purchaseOrder.number}</span>
                    </Cell>
                    <Cell numeric>{formatCents(i.total.cents)}</Cell>
                    <Cell>
                      <Tag state={i.matched ? 'done' : 'attention'}>
                        {i.matched ? 'Matches' : 'Differences'}
                      </Tag>
                    </Cell>
                    <Cell>
                      <Tag state={documentTag(i.status)}>{documentLabel(i.status)}</Tag>
                    </Cell>
                  </Row>
                )}
              </TableBody>
            </Table>
          </TableScroll>
        ) : null}
      </Section>
    </Frame>
  );
}

export function SuppliersPage() {
  const suppliers = useQuery({
    queryKey: ['suppliers', 'all'],
    queryFn: () => searchSuppliers('', true),
  });
  return (
    <Frame>
      <Section title="Supplier statements">
        {suppliers.isPending ? <LoadingBlock label="Loading suppliers" rows={3} /> : null}
        {suppliers.isError ? (
          <LoadFailed
            error={suppliers.error}
            fallback="Suppliers couldn't be loaded."
            onRetry={() => void suppliers.refetch()}
          />
        ) : null}
        {suppliers.isSuccess ? (
          suppliers.data.length === 0 ? (
            <p className={styles.meta}>
              No suppliers yet. Add one in Settings, Stores and suppliers.
            </p>
          ) : (
            <ul>
              {suppliers.data.map((s) => (
                <li key={s.id}>
                  <Link
                    to="/procurement/suppliers/$supplierId/statement"
                    params={{ supplierId: s.id }}
                  >
                    {s.name}
                  </Link>
                  {s.status === 'active' ? '' : ' (switched off)'}
                </li>
              ))}
            </ul>
          )
        ) : null}
      </Section>
    </Frame>
  );
}
